import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, ChevronDown, ChevronUp, Plus, Search, DollarSign, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { getCatalogItem, updateCatalogItem, addCustomerPrice, removeCustomerPrice } from '@/lib/api';
import { toast } from 'sonner';

import { Screen, CommandBar } from '@/components/data';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { validateField, formatApiError } from '@/lib/validators';
import {
  DynamicFieldForm,
  usePlyChangeHandler,
  PlyChangeDialog,
} from '@/components/DynamicFieldRenderer';
import { useIsMobile } from '@/hooks/useIsMobile';
import PhoneEditCatalogItem from '@/pages/Catalog/PhoneEditCatalogItem';

const EditCatalogItemPage = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [priceToRemove, setPriceToRemove] = useState(null);

  // Core fields
  const [itemName, setItemName] = useState('');
  const [productType, setProductType] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [minStockThreshold, setMinStockThreshold] = useState('0');
  const [status, setStatus] = useState('active');

  // Dynamic field values (job card only)
  const [jobCardFieldValues, setJobCardFieldValues] = useState({});
  const [jobCardFields, setJobCardFields] = useState([]);

  // Ply handler
  const { plyDialog, handlePlyChange, confirmPlyChange, cancelPlyChange } = usePlyChangeHandler(
    jobCardFieldValues,
    setJobCardFieldValues
  );

  // Customer pricing
  const [showPricing, setShowPricing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [existingPrices, setExistingPrices] = useState([]);
  const [newPriceCustomer, setNewPriceCustomer] = useState('');
  const [newPriceValue, setNewPriceValue] = useState('');

  useEffect(() => {
    fetchAll();
  }, [itemId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const itemData = await getCatalogItem(itemId);

      // Populate form
      setItemName(itemData.name || '');
      setProductType(itemData.product_type || '');
      setBasePrice(itemData.base_price_per_piece != null ? String(itemData.base_price_per_piece) : '');
      setMinStockThreshold(String(itemData.min_stock_threshold || 0));
      setStatus(itemData.status || 'active');
      setJobCardFieldValues(itemData.job_card_fields || {});
      setExistingPrices(itemData.customer_prices || []);

      // Load job card config for this product type
      if (itemData.product_type) {
        try {
          const configRes = await api.get(`/product-configs/${itemData.product_type}/job-card-fields`);
          setJobCardFields(configRes.data?.fields || []);
        } catch {
          setJobCardFields([]);
        }
      }
    } catch (error) {
      toast.error('Failed to load catalog item');
      navigate('/catalog');
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers for pricing
  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get('/customers', { params: { search: customerSearch, limit: 50 } });
      setCustomers(res.data.customers || []);
    } catch {}
  }, [customerSearch]);

  useEffect(() => { if (showPricing) fetchCustomers(); }, [fetchCustomers, showPricing]);

  const handleAddCustomerPrice = async () => {
    const amountErr = validateField('amount', newPriceValue);
    if (amountErr) { toast.error('Please enter a valid price'); return; }
    if (!newPriceCustomer || !newPriceValue) {
      toast.error('Select customer and enter price');
      return;
    }
    try {
      await addCustomerPrice(itemId, {
        customer_id: newPriceCustomer,
        price_per_piece: parseFloat(newPriceValue),
      });
      toast.success('Customer price added');
      setNewPriceCustomer('');
      setNewPriceValue('');
      // Refresh item
      const refreshed = await getCatalogItem(itemId);
      setExistingPrices(refreshed.customer_prices || []);
    } catch (error) {
      toast.error('Failed to add customer price');
    }
  };

  const handleRemovePrice = (customerId) => setPriceToRemove(customerId);

  const confirmRemovePrice = async () => {
    const customerId = priceToRemove;
    setPriceToRemove(null);
    if (!customerId) return;
    try {
      await removeCustomerPrice(itemId, customerId);
      toast.success('Price removed');
      setExistingPrices(prev => prev.filter(p => p.customer_id !== customerId));
    } catch {
      toast.error('Failed to remove price');
    }
  };

  const submit = async () => {
    if (!itemName) {
      toast.error('Item name is required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: itemName,
        base_price_per_piece: basePrice ? parseFloat(basePrice) : null,
        min_stock_threshold: parseInt(minStockThreshold) || 0,
        status,
        job_card_fields: jobCardFieldValues,
      };
      await updateCatalogItem(itemId, payload);
      toast.success('Catalog item updated successfully');
      navigate(`/catalog/${itemId}`);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save item'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); submit(); };

  const setFieldValue = (fieldName, value) => setJobCardFieldValues(prev => ({ ...prev, [fieldName]: value }));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const dialogs = (
    <>
      <PlyChangeDialog open={plyDialog.open} onConfirm={confirmPlyChange} onCancel={cancelPlyChange} oldPly={plyDialog.oldPly} newPly={plyDialog.newPly} />
      <AlertDialog open={!!priceToRemove} onOpenChange={() => setPriceToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this customer price?</AlertDialogTitle>
            <AlertDialogDescription>That customer goes back to paying the default price on new orders.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemovePrice} className="bg-red-600 text-white hover:bg-red-700">
              Remove price
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  // 48px controls under their labels below lg; the same state, the same payload
  if (isMobile) {
    return (
      <>
        <PhoneEditCatalogItem
          onBack={() => navigate(`/catalog/${itemId}`)}
          itemName={itemName} setItemName={setItemName} productType={productType}
          status={status} setStatus={setStatus}
          basePrice={basePrice} setBasePrice={setBasePrice}
          minStockThreshold={minStockThreshold} setMinStockThreshold={setMinStockThreshold}
          jobCardFields={jobCardFields} jobCardFieldValues={jobCardFieldValues}
          onFieldChange={setFieldValue} onPlyChange={handlePlyChange}
          showPricing={showPricing} setShowPricing={setShowPricing}
          existingPrices={existingPrices} customers={customers} onCustomerSearch={setCustomerSearch}
          newPriceCustomer={newPriceCustomer} setNewPriceCustomer={setNewPriceCustomer}
          newPriceValue={newPriceValue} setNewPriceValue={setNewPriceValue}
          onAddPrice={handleAddCustomerPrice} onRemovePrice={handleRemovePrice}
          saving={saving} onSubmit={submit}
        />
        {dialogs}
      </>
    );
  }

  return (
    <Screen className="mx-auto w-full max-w-5xl">
      <CommandBar
        title="Edit catalog item"
        subtitle="Changes apply to future orders only — existing orders keep what they were placed with"
        onBack={() => navigate(`/catalog/${itemId}`)}
      />

      <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="space-y-4">

          {/* Basic Info */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">Basic Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium">Item Name *</Label>
                  <Input value={itemName} onChange={e => setItemName(e.target.value)} required />
                </div>
                <div>
                  <Label className="text-sm font-medium">Product Type</Label>
                  <Input value={productType} disabled className="bg-slate-100" />
                  <p className="text-xs text-slate-400 mt-1">Product type cannot be changed after creation</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <Select value={status} onValueChange={v => setStatus(v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Base Price per Piece (optional)</Label>
                  <Input type="number" step="0.01" value={basePrice}
                    onChange={e => setBasePrice(e.target.value)} placeholder="₹ 0.00" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Min Stock Threshold</Label>
                  <Input type="number" value={minStockThreshold}
                    onChange={e => setMinStockThreshold(e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Job Card Fields ONLY (no order specs) - using shared renderer */}
          {jobCardFields.length > 0 && (
            <Card className="border-blue-200 shadow-sm bg-blue-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  Job Card Template
                </CardTitle>
                <p className="text-xs text-slate-500">
                  Fill technical specifications. These will be pre-filled on job cards for future orders.
                </p>
              </CardHeader>
              <CardContent>
                <DynamicFieldForm
                  fields={jobCardFields}
                  values={jobCardFieldValues}
                  onChange={setFieldValue}
                  columns={2}
                  showEmptyState={false}
                  onPlyChange={handlePlyChange}
                  orderType={productType}
                />
              </CardContent>
            </Card>
          )}

          {/* Customer Pricing */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="cursor-pointer" onClick={() => setShowPricing(!showPricing)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Customer Pricing
                  {existingPrices.length > 0 && (
                    <Badge variant="outline" className="text-xs">{existingPrices.length} linked</Badge>
                  )}
                </CardTitle>
                {showPricing ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardHeader>
            {showPricing && (
              <CardContent className="space-y-4">
                {existingPrices.length > 0 && (
                  <div className="space-y-2">
                    {existingPrices.map(cp => (
                      <div key={cp.customer_id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{cp.customer_name}</p>
                          <p className="text-xs text-green-600 font-semibold">₹{cp.price_per_piece?.toFixed(2)}/piece</p>
                        </div>
                        <button type="button" onClick={() => handleRemovePrice(cp.customer_id)}
                          className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Customer</Label>
                    <div className="relative mb-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input placeholder="Search customers..." value={customerSearch}
                        onChange={e => setCustomerSearch(e.target.value)} className="pl-9 h-9 text-sm" />
                    </div>
                    <Select value={newPriceCustomer} onValueChange={v => setNewPriceCustomer(v)}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Select customer..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.filter(c => !existingPrices.some(cp => cp.customer_id === c.id)).map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.customer_name} {c.firm_name ? `(${c.firm_name})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Price per Piece (₹)</Label>
                    <Input type="number" step="0.01" value={newPriceValue}
                      onChange={e => setNewPriceValue(e.target.value)} placeholder="0.00" className="h-9" />
                    <Button type="button" variant="outline" size="sm" className="mt-1 w-full" onClick={handleAddCustomerPrice}>
                      <Plus className="w-3 h-3 mr-1" /> Add Price
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate(`/catalog/${itemId}`)}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-brand-blue hover:bg-blue-600">
              {saving ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </div>
        </div>
      </form>
      {dialogs}
    </Screen>
  );
};

export default EditCatalogItemPage;
